import { useEffect, useState, useCallback, useRef } from "react";
import { api, API_BASE } from "@/lib/api";
import { devLog } from "@/lib/log";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import Disclaimer from "@/components/Disclaimer";
import PushToggle from "@/components/PushToggle";
import DigestScheduleToggle from "@/components/DigestScheduleToggle";
import ThresholdDefaultTile from "@/components/ThresholdDefaultTile";
import { resizeImage } from "@/lib/resizeImage";
import {
  Save, LogOut, Trash2, Plus, Upload, X as XIcon,
  Image as ImageIcon2, ImagePlus, Tag, Bell,
  BadgeAlert, Users2, ChevronDown, ShieldCheck, ShieldAlert,
  Camera, Files,
} from "lucide-react";
import { Link } from "react-router-dom";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { useApp } from "@/contexts/AppContext";
import AccountantInviteQR from "@/components/AccountantInviteQR";
import DangerZone from "@/components/DangerZone";
import FeedbackButton from "@/components/FeedbackButton";
import BrandFooterLine from "@/components/BrandFooterLine";
import BiSelect from "@/components/BiSelect";

const TRADES = [
  "General Builder", "Carpenter", "Electrician", "Plumber", "Painter",
  "Concreter", "Landscaper", "Civil Contractor", "Roofer", "Tiler", "Other",
];

// ─── Small building blocks (co-located: they only exist to declutter this page) ─────────

/**
 * `GroupLabel` — a tiny uppercase heading that separates broad domains
 * (Business / Preferences / Sharing). Purely decorative — no interactivity.
 */
function GroupLabel({ children }) {
  return (
    <div className="pt-2 pb-0.5 px-1 text-[10px] uppercase tracking-[0.24em] text-white/40 font-heading">
      {children}
    </div>
  );
}

/**
 * `Section` — a glove-friendly collapsible row with an icon, title, optional
 * subtitle, and a chevron. The child content sits BELOW the header when
 * expanded so nested `bi-surface` components (PushToggle, DigestScheduleToggle,
 * etc.) can render unchanged as the "body".
 *
 * Props:
 *  - `testid`   → root data-testid (e.g. `section-branding`)
 *  - `defaultOpen` → true only for primary sections
 *  - `hint`     → optional inline right-aligned status text (e.g. "Save changes here")
 */
function Section({ testid, icon: Icon, title, subtitle, hint, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div data-testid={testid} className="space-y-2">
      <button
        type="button"
        data-testid={`${testid}-toggle`}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="w-full min-h-[56px] px-3.5 py-2.5 flex items-center justify-between gap-3 bi-surface hover:brightness-110 active:scale-[0.995] transition rounded-2xl text-left"
      >
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <div className="w-9 h-9 rounded-lg bg-[#FF9500]/15 text-[#FF9500] flex items-center justify-center shrink-0">
            <Icon className="w-4 h-4" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-heading font-bold text-sm text-white truncate">{title}</div>
            {subtitle && (
              <div className="text-[11px] text-white/45 truncate mt-0.5">{subtitle}</div>
            )}
          </div>
          {hint && (
            <span className="hidden sm:inline text-[10px] uppercase tracking-wider text-white/35 font-heading shrink-0">
              {hint}
            </span>
          )}
        </div>
        <ChevronDown
          className={`w-4 h-4 text-white/50 transition-transform shrink-0 ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && <div className="space-y-3">{children}</div>}
    </div>
  );
}

// ─── Main ──────────────────────────────────────────────────────────────────────────

export default function Settings() {
  const { logout, user } = useAuth();
  const { categories, reloadJobs, reloadProfile } = useApp();
  const [profile, setProfile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [newCat, setNewCat] = useState("");

  const loadProfile = useCallback(async () => {
    try {
      const { data } = await api.get("/profile");
      setProfile(data);
    } catch (e) {
      devLog.warn("profile load failed", e);
    }
  }, []);
  useEffect(() => { loadProfile(); }, [loadProfile]);

  if (!profile) return <div className="py-10 text-white/50 text-center">Loading…</div>;

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/profile", profile);
      await reloadProfile();
      toast.success("Saved");
    } catch { toast.error("Save failed"); }
    finally { setBusy(false); }
  };

  const addCat = async () => {
    if (!newCat.trim()) return;
    try {
      await api.post("/categories", {
        name: newCat,
        code: newCat.slice(0, 3).toUpperCase(),
        deduction_type: "materials",
      });
      setNewCat("");
      await reloadJobs();
      toast.success("Category added");
    } catch { toast.error("Failed"); }
  };
  const delCat = async (id) => {
    try {
      await api.delete(`/categories/${id}`);
      await reloadJobs();
      toast.success("Deleted");
    } catch { toast.error("Failed"); }
  };

  const hasAccountant = Boolean(profile.accountant_email || profile.accountant_name);
  const hasLogo = Boolean(profile.logo_path);
  const hasAvatar = Boolean(profile.avatar_path);

  return (
    <div className="space-y-3">
      {/* ── Header ── */}
      <div className="pt-1">
        <h1 className="font-heading text-2xl font-extrabold">Settings</h1>
        <div className="text-sm text-white/60 truncate">
          Signed in as <b>{user?.email}</b>
        </div>
      </div>

      {/* ══════ BUSINESS ══════ */}
      <GroupLabel>Business</GroupLabel>

      {/* Primary card — always open. This is where tradies save their ABN/GST
         setup and it's the highest-frequency action on this page. */}
      <section className="bi-surface p-4 space-y-3" data-testid="section-business-profile">
        <div className="flex items-baseline justify-between">
          <div className="font-heading font-bold">Business profile</div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-heading">
            Save changes below
          </div>
        </div>
        <input
          data-testid="set-biz-name"
          className="w-full bi-input"
          placeholder="Business name"
          value={profile.business_name || ""}
          onChange={(e) => setProfile({ ...profile, business_name: e.target.value })}
        />
        {/* Personal-tax-only header field. Kept separate from the business
            name so a sole trader can put their legal name on their Personal
            Tax PDF header even if the business trades under something else.
            Business PDFs (BAS/TPAR/Accountant Pack) still use `business_name`. */}
        <div>
          <input
            data-testid="set-individual-name"
            className="w-full bi-input"
            placeholder="Your full name (for Personal Tax packs)"
            value={profile.individual_name || ""}
            onChange={(e) => setProfile({ ...profile, individual_name: e.target.value })}
          />
          <p className="mt-1 text-[11px] text-white/45 leading-snug">
            Appears as the primary heading on your Personal Tax PDF. Leave
            blank and we&apos;ll fall back to your account name.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <input
            data-testid="set-abn"
            className="bi-input"
            placeholder="ABN"
            value={profile.abn || ""}
            onChange={(e) => setProfile({ ...profile, abn: e.target.value })}
          />
          <BiSelect
            data-testid="set-trade"
            className="bi-input"
            value={profile.trade_type || "General Builder"}
            onChange={(e) => setProfile({ ...profile, trade_type: e.target.value })}
          >
            {TRADES.map((t) => <option key={t}>{t}</option>)}
          </BiSelect>
        </div>
        <input
          data-testid="set-address"
          className="w-full bi-input"
          placeholder="Business address"
          value={profile.address || ""}
          onChange={(e) => setProfile({ ...profile, address: e.target.value })}
        />
        <input
          data-testid="set-phone"
          className="w-full bi-input"
          placeholder="Phone"
          value={profile.phone || ""}
          onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
        />
        <label className="flex items-center gap-3 h-14 px-3 bi-surface-2 rounded-lg">
          <input
            type="checkbox"
            checked={!!profile.default_gst}
            onChange={(e) => setProfile({ ...profile, default_gst: e.target.checked })}
            className="w-5 h-5 accent-[#FF9500]"
            data-testid="set-gst"
          />
          <span className="text-sm">Auto-calc GST at 1/11th</span>
        </label>
        <button
          data-testid="set-save"
          onClick={save}
          disabled={busy}
          className="w-full h-14 rounded-xl bi-accent-btn flex items-center justify-center gap-2 disabled:opacity-60"
        >
          <Save className="w-4 h-4" /> Save profile
        </button>
      </section>

      {/* Secondary business options — collapsed by default. */}
      <Section
        testid="section-branding"
        icon={ImagePlus}
        title="Branding"
        subtitle={hasLogo || hasAvatar ? "Logo & avatar attached" : "Add logo and avatar"}
      >
        <section className="bi-surface p-4" data-testid="branding-section">
          <div className="grid grid-cols-2 gap-3">
            <ProfileImageField
              kind="logo"
              label="Business logo"
              hint="JPG/PNG/WEBP, ≤5 MB. Appears on PDF Accountant Pack & invoices."
              path={profile.logo_path}
              onUpdated={(p) => {
                setProfile({ ...profile, logo_path: p });
                reloadProfile();
              }}
            />
            <ProfileImageField
              kind="avatar"
              label="Profile avatar"
              hint="JPG/PNG/WEBP, ≤5 MB. Shown in top-right menu."
              path={profile.avatar_path}
              onUpdated={(p) => {
                setProfile({ ...profile, avatar_path: p });
                // Push the new avatar_path into AppContext immediately so the
                // top-bar avatar updates without a full profile save.
                reloadProfile();
              }}
            />
          </div>
        </section>
      </Section>

      <Section
        testid="section-categories"
        icon={Tag}
        title="Categories"
        subtitle={`${categories?.length || 0} deduction ${categories?.length === 1 ? "category" : "categories"}`}
      >
        <section className="bi-surface p-4 space-y-3">
          <div className="space-y-1.5">
            {categories.map((c) => (
              <div
                key={c.id}
                className="flex items-center justify-between bi-surface-2 px-3 py-2 rounded-lg"
              >
                <div>
                  <span className="font-medium">{c.name}</span>{" "}
                  <span className="text-xs text-white/40">({c.code})</span>
                </div>
                <button
                  data-testid={`cat-del-${c.id}`}
                  onClick={() => delCat(c.id)}
                  className="w-8 h-8 rounded-lg bi-ghost-btn flex items-center justify-center text-red-300"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <input
              data-testid="cat-new"
              value={newCat}
              onChange={(e) => setNewCat(e.target.value)}
              className="flex-1 bi-input"
              placeholder="New category name"
            />
            <button
              data-testid="cat-add"
              onClick={addCat}
              className="h-14 w-14 rounded-xl bi-accent-btn flex items-center justify-center"
            >
              <Plus className="w-5 h-5" />
            </button>
          </div>
        </section>
      </Section>

      {/* ══════ ALERTS & DIGEST ══════ */}
      <GroupLabel>Alerts &amp; digest</GroupLabel>

      <Section
        testid="section-notifications"
        icon={Bell}
        title="Notifications"
        subtitle="Job-health push alerts"
      >
        <PushToggle />
      </Section>

      <Section
        testid="section-cost-alerts"
        icon={BadgeAlert}
        title="Cost alerts & digest"
        subtitle="Digest schedule + default threshold · estimates only"
      >
        <DigestScheduleToggle />
        <ThresholdDefaultTile />
        <div className="px-1">
          <Disclaimer
            testid="cost-alerts-disclaimer"
            text="Cost alerts are estimates only. Confirm actual overruns and any tax implications with your accountant."
          />
        </div>
      </Section>

      {/* ══════ SHARING ══════ */}
      <GroupLabel>Sharing</GroupLabel>

      <Section
        testid="section-accountant"
        icon={Users2}
        title="Accountant"
        subtitle={hasAccountant ? `Linked · ${profile.accountant_email || profile.accountant_name}` : "Prefill BAS & TPAR share sheet"}
      >
        <section className="bi-surface p-4 space-y-3" data-testid="accountant-section">
          <p className="text-[11px] text-white/50 leading-snug">
            Save your accountant here and the <b>Email</b> button on the BAS &amp; TPAR cards will drop straight into a pre-addressed message.
          </p>
          <input
            data-testid="set-accountant-name"
            className="w-full bi-input"
            placeholder="Accountant name (optional)"
            value={profile.accountant_name || ""}
            onChange={(e) => setProfile({ ...profile, accountant_name: e.target.value })}
          />
          <input
            data-testid="set-accountant-email"
            className="w-full bi-input"
            type="email"
            placeholder="accountant@firm.com.au"
            value={profile.accountant_email || ""}
            onChange={(e) => setProfile({ ...profile, accountant_email: e.target.value })}
          />
          <button
            data-testid="set-accountant-save"
            onClick={save}
            disabled={busy}
            className="w-full h-11 rounded-xl bi-ghost-btn text-xs font-heading font-bold uppercase tracking-wider inline-flex items-center justify-center gap-2 disabled:opacity-60"
          >
            <Save className="w-3.5 h-3.5" /> Save accountant
          </button>
        </section>
        <AccountantInviteQR />
      </Section>

      {/* ══════ PRIVACY & ACCOUNT ══════ */}
      <GroupLabel>Privacy &amp; account</GroupLabel>

      <Section
        testid="section-privacy"
        icon={ShieldCheck}
        title="Privacy statement"
        subtitle="What we store · what we don't do with it"
      >
        <section className="bi-surface p-4 space-y-3" data-testid="privacy-section">
          <p className="text-[13px] text-white/75 leading-relaxed">
            We keep only what BuildIntel AI needs to help you log receipts, run
            jobs and prep BAS — your business details, entries, photos,
            summaries. <b className="text-white">We do not sell your data</b>,
            and you can delete your account any time from the Danger zone below.
          </p>
          <Link
            to="/privacy"
            data-testid="privacy-link"
            className="w-full h-11 rounded-xl bi-ghost-btn text-xs font-heading font-bold uppercase tracking-wider inline-flex items-center justify-center gap-2"
          >
            <ShieldCheck className="w-3.5 h-3.5 text-[#FF9500]" />
            Read the full privacy statement
          </Link>
        </section>
      </Section>

      <Section
        testid="section-danger-zone"
        icon={ShieldAlert}
        title="Danger zone"
        subtitle="Permanently delete your account and every entry"
      >
        <DangerZone />
      </Section>

      {/* Feedback — a low-friction path for testers to reach the operator.
          Kept as a stand-alone row (not inside a collapsible Section) so
          it's always one tap away and hard to miss during soft launch. */}
      <FeedbackButton />

      {/* ── Footer ── */}
      <div className="pt-2">
        <Disclaimer text="BuildIntel AI is a helper tool for tradies. It doesn't replace advice from a registered accountant or tax agent." />
      </div>

      <BrandFooterLine testId="settings-brand-footer" className="text-center pt-1" />

      <button
        data-testid="settings-logout"
        onClick={logout}
        className="w-full h-14 rounded-xl bi-ghost-btn flex items-center justify-center gap-2 text-red-300"
      >
        <LogOut className="w-4 h-4" /> Log out
      </button>
    </div>
  );
}

// ─── Profile image field (unchanged from prior implementation) ─────────────────────

function ProfileImageField({ kind, label, hint, path, onUpdated }) {
  const inputRef = useRef(null);
  const cameraRef = useRef(null);
  const libraryRef = useRef(null);
  const [busy, setBusy] = useState(false);
  // Dark "choose source" sheet — matches the Jobs attachments UI so the
  // Branding logo/avatar upload no longer punches through to the OS's pale
  // native "Photo Library / Take Photo / Choose File" picker menu.
  const [sourceOpen, setSourceOpen] = useState(false);
  const upload = async (files) => {
    const file = files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      // Client-side resize before upload. Logos rarely need more than ~1024px on
      // the long edge for print — anything larger is bandwidth waste on tradie
      // 4G. Falls back to the original file for non-image types or if the
      // browser can't decode the source (see resizeImage.js internals).
      let payloadFile = file;
      try {
        payloadFile = await resizeImage(file, { maxEdge: 1024, quality: 0.85 });
      } catch {
        payloadFile = file;
      }
      const fd = new FormData();
      fd.append("file", payloadFile);
      const { data } = await api.post(`/profile/${kind}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onUpdated(data.path);
      toast.success(`${label} updated`);
    } catch (e) {
      devLog.warn(`upload ${kind} failed`, e);
      const detail = e.response?.data?.detail || "Upload failed";
      toast.error(typeof detail === "string" ? detail : "Upload failed");
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    if (!path) return;
    try {
      await api.delete(`/profile/${kind}`);
      onUpdated(null);
    } catch {
      toast.error("Remove failed");
    }
  };
  return (
    <div className="bi-surface-2 rounded-xl p-3" data-testid={`profile-${kind}-field`}>
      <div className="text-[10px] uppercase tracking-wider text-white/50 font-heading">{label}</div>
      <div className="mt-2 relative aspect-square rounded-lg overflow-hidden border border-white/10 bg-black/30 flex items-center justify-center">
        {path ? (
          <img
            src={`${API_BASE}/api/files/${path}`}
            alt={label}
            className="w-full h-full object-cover"
            data-testid={`profile-${kind}-img`}
          />
        ) : (
          <ImageIcon2 className="w-8 h-8 text-white/20" />
        )}
      </div>
      {/* Three hidden inputs — one per source. Each is fired from the dark
          "choose source" sheet below rather than the button directly, so the
          OS's pale picker menu is bypassed on iOS/Chrome. The legacy
          `profile-${kind}-input` testid stays on the "Choose File" input for
          continuity with existing tests. */}
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={(e) => { upload(e.target.files); e.target.value = ""; }}
        data-testid={`profile-${kind}-input`}
      />
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => { upload(e.target.files); e.target.value = ""; }}
        data-testid={`profile-${kind}-camera-input`}
      />
      <input
        ref={libraryRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => { upload(e.target.files); e.target.value = ""; }}
        data-testid={`profile-${kind}-library-input`}
      />
      <button
        type="button"
        onClick={() => setSourceOpen(true)}
        disabled={busy}
        data-testid={`profile-${kind}-upload`}
        className="mt-2 w-full h-10 rounded-lg bi-ghost-btn text-xs inline-flex items-center justify-center gap-1.5 disabled:opacity-50"
      >
        <Upload className="w-3.5 h-3.5" /> {busy ? "Uploading…" : path ? "Replace" : "Upload"}
      </button>
      {path && (
        <button
          type="button"
          onClick={remove}
          data-testid={`profile-${kind}-remove`}
          className="mt-1.5 w-full h-9 rounded-lg bi-ghost-btn text-[11px] text-red-300 inline-flex items-center justify-center gap-1.5"
        >
          <XIcon className="w-3.5 h-3.5" /> Remove
        </button>
      )}

      {/* Dark bottom sheet — matches the Jobs attachment source picker */}
      <Sheet open={sourceOpen} onOpenChange={setSourceOpen}>
        <SheetContent
          side="bottom"
          className="bg-[#1F1F1F] border-t border-white/10 text-white rounded-t-2xl"
        >
          <SheetHeader className="text-left">
            <SheetTitle className="font-heading">Update {label.toLowerCase()}</SheetTitle>
            <SheetDescription className="text-white/50">
              JPG, PNG or WebP images work best. Camera captures are cropped to a square.
            </SheetDescription>
          </SheetHeader>
          <div className="mt-4 grid gap-2 pb-[env(safe-area-inset-bottom)]">
            <button
              type="button"
              data-testid={`profile-${kind}-source-camera`}
              onClick={() => { setSourceOpen(false); cameraRef.current?.click(); }}
              className="w-full h-14 px-4 rounded-xl bi-surface hover:brightness-110 active:scale-[0.99] transition flex items-center gap-3 border border-transparent hover:border-[#FF9500]/30"
            >
              <span className="w-9 h-9 rounded-lg bg-[#FF9500]/15 text-[#FF9500] inline-flex items-center justify-center">
                <Camera className="w-4 h-4" />
              </span>
              <span className="flex-1 text-left">
                <div className="text-sm font-heading font-bold">Take Photo</div>
                <div className="text-[11px] text-white/50">Opens the camera on your phone</div>
              </span>
            </button>
            <button
              type="button"
              data-testid={`profile-${kind}-source-library`}
              onClick={() => { setSourceOpen(false); libraryRef.current?.click(); }}
              className="w-full h-14 px-4 rounded-xl bi-surface hover:brightness-110 active:scale-[0.99] transition flex items-center gap-3 border border-transparent hover:border-[#FF9500]/30"
            >
              <span className="w-9 h-9 rounded-lg bg-[#FF9500]/15 text-[#FF9500] inline-flex items-center justify-center">
                <ImageIcon2 className="w-4 h-4" />
              </span>
              <span className="flex-1 text-left">
                <div className="text-sm font-heading font-bold">Photo Library</div>
                <div className="text-[11px] text-white/50">Pick a photo from your gallery</div>
              </span>
            </button>
            <button
              type="button"
              data-testid={`profile-${kind}-source-files`}
              onClick={() => { setSourceOpen(false); inputRef.current?.click(); }}
              className="w-full h-14 px-4 rounded-xl bi-surface hover:brightness-110 active:scale-[0.99] transition flex items-center gap-3 border border-transparent hover:border-[#FF9500]/30"
            >
              <span className="w-9 h-9 rounded-lg bg-[#FF9500]/15 text-[#FF9500] inline-flex items-center justify-center">
                <Files className="w-4 h-4" />
              </span>
              <span className="flex-1 text-left">
                <div className="text-sm font-heading font-bold">Choose File</div>
                <div className="text-[11px] text-white/50">JPG, PNG or WebP from anywhere on your device</div>
              </span>
            </button>
          </div>
        </SheetContent>
      </Sheet>
      <p className="mt-1 text-[10px] text-white/40 leading-snug">{hint}</p>
    </div>
  );
}
